import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * @fileoverview FollowButton — Loci Follow/Following toggle
 *
 * Amber outline when unfollowed, flips to filled amber with white text when
 * followed. Accessible toggle semantics via `aria-pressed`; focus-visible ring
 * meets WCAG AA on both theme modes. Parents control the `following` state;
 * this component is purely presentational + interactive.
 */
import React, { forwardRef } from 'react';
import { UserPlus, UserCheck, Loader2 } from 'lucide-react';
import { useTheme } from '../../../theme';
const SIZE_PX = 36;
/**
 * FollowButton — follow/following toggle from the LOCI · CLT design system.
 */
export const FollowButton = forwardRef(({ following, loading = false, disabled = false, label, onClick, onMouseEnter, onMouseLeave, style, ...rest }, ref) => {
    const theme = useTheme();
    const [hovered, setHovered] = React.useState(false);
    const isDisabled = disabled || loading;
    const resolvedLabel = label ?? (following ? 'Following' : 'Follow');
    const Icon = loading ? Loader2 : following ? UserCheck : UserPlus;
    const baseStyles = {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        height: SIZE_PX,
        paddingInline: 14,
        borderRadius: 999,
        fontFamily: theme.typography.fontFamilyDisplay ?? theme.typography.fontFamily,
        fontWeight: theme.typography.fontWeight.semibold,
        fontSize: theme.typography.fontSize.small,
        letterSpacing: '0.01em',
        lineHeight: 1,
        border: `1px solid ${theme.colors.accent}`,
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        opacity: isDisabled && !loading ? 0.5 : 1,
        transition: 'background-color var(--dur-standard, 180ms) var(--ease-state, ease), color var(--dur-standard, 180ms) var(--ease-state, ease), border-color var(--dur-standard, 180ms) var(--ease-state, ease), filter var(--dur-standard, 180ms) var(--ease-state, ease)',
        appearance: 'none',
        userSelect: 'none',
        ...style,
    };
    if (following) {
        baseStyles.backgroundColor = theme.colors.accent;
        baseStyles.color = theme.colors.accentForeground ?? '#FFFFFF';
        // Subtle darken on hover to preview the "Unfollow" affordance.
        if (hovered && !isDisabled) {
            baseStyles.filter = 'brightness(0.92)';
        }
    }
    else {
        baseStyles.backgroundColor =
            hovered && !isDisabled ? `${theme.colors.accent}1A` : 'transparent';
        baseStyles.color = theme.colors.accent;
    }
    return (_jsxs("button", { ref: ref, type: "button", "aria-pressed": following, "aria-label": following ? 'Unfollow artist' : 'Follow artist', "aria-busy": loading || undefined, disabled: isDisabled, onClick: isDisabled ? undefined : onClick, onMouseEnter: (e) => {
            setHovered(true);
            onMouseEnter?.(e);
        }, onMouseLeave: (e) => {
            setHovered(false);
            onMouseLeave?.(e);
        }, style: baseStyles, "data-testid": "follow-button", "data-following": following ? 'true' : 'false', ...rest, children: [_jsx(Icon, { size: 16, strokeWidth: 1.8, "aria-hidden": "true", 
                // `motion-safe:` ensures the spin is suppressed under
                // `prefers-reduced-motion: reduce`; the Loader2 icon stays visible.
                className: loading ? 'motion-safe:animate-spin' : undefined }), _jsx("span", { children: resolvedLabel })] }));
});
FollowButton.displayName = 'FollowButton';
